<script lang="ts" setup>
import MainLayout from '@/common/components/MainLayout.vue';
import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import Tab from 'primevue/tab';
import TabPanels from 'primevue/tabpanels';
import TabPanel from 'primevue/tabpanel';
import InputText from 'primevue/inputtext';
import InputGroup from 'primevue/inputgroup';
import InputNumber from 'primevue/inputnumber';
import Message from 'primevue/message';
import Fieldset from 'primevue/fieldset';
import InputGroupAddon from 'primevue/inputgroupaddon';
import FeatureFlagComponent from '@/featureFlags/components/FeatureFlagComponent.vue';
import ReportComponent from '@/reports/components/ReportComponent.vue';
import DashboardComponentTypeConfig from '@/dashboards/components/DashboardComponentTypeConfig.vue';

import { Card, Button, useToast } from 'primevue';
import { computed, ref, onMounted } from 'vue';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { storeToRefs } from 'pinia';

import { useSettingStore } from 'shared/setting/stores/settingStore';
import { useSaveSettings } from './api/saveSettings';
import type { SettingRequest } from './api/models/settingRequest';
import { useAuthStore } from '@/auth/store/authStore';
import { useRefreshSettingStore } from './stores/refreshSettingStore';

const toast = useToast();
const authStore = useAuthStore();
const refreshSettingStore = useRefreshSettingStore();

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);
const pageTranslations = computed(() => translations.value.systemSettings.page);

const settingStore = useSettingStore();

const saveSettings = useSaveSettings();

const nameFormat = ref<string>(settingStore.setting.sharedClient.storageArea.nameFormat);
const nameFormatInitial = computed(() => settingStore.setting.sharedClient.storageArea.nameFormat);
const nameFormatChanged = computed(() => nameFormat.value !== nameFormatInitial.value);
const nameFormatError = computed(() => {
  if (nameFormat.value === '') {
    return pageTranslations.value.storagearea.nameFormatRequired;
  } else if (nameFormat.value.indexOf('{#}') === -1) {
    return pageTranslations.value.storagearea.nameFormatMissingSequence;
  } else {
    return '';
  }
});

const logYardLength = ref<number>(settingStore.setting.sharedClient.storageArea.logYardLength);
const logYardLengthInitial = computed(
  () => settingStore.setting.sharedClient.storageArea.logYardLength,
);
const logYardLengthChanged = computed(() => logYardLength.value !== logYardLengthInitial.value);

const logYardWidth = ref<number>(settingStore.setting.sharedClient.storageArea.logYardWidth);
const logYardWidthInitial = computed(
  () => settingStore.setting.sharedClient.storageArea.logYardWidth,
);
const logYardWidthChanged = computed(() => logYardWidth.value !== logYardWidthInitial.value);

const logYardSideArea = ref<number>(settingStore.setting.sharedClient.storageArea.logYardSideArea);
const logYardSideAreaInitial = computed(
  () => settingStore.setting.sharedClient.storageArea.logYardSideArea,
);
const logYardSideAreaChanged = computed(
  () => logYardSideArea.value !== logYardSideAreaInitial.value,
);

async function save() {
  if (nameFormatError.value) {
    return;
  }

  const newSetting: SettingRequest = {
    sharedClient: {
      storageArea: {
        nameFormat: nameFormat.value,
        logYardLength: logYardLength.value,
        logYardWidth: logYardWidth.value,
        logYardSideArea: logYardSideArea.value,
      },
      map: {
        minZoom: minZoom.value,
      },
    },
  };

  const response = await saveSettings.execute(newSetting);
  if (response.ok) {
    toast.add({
      severity: 'success',
      summary: translations.value.generic.success,
      detail: pageTranslations.value.settingsSaved,
      life: 3000,
    });

    settingStore.setting = response.setting;
  }
}

onMounted(async () => {
  await refreshSettingStore.initialize();

  // Set the value here so that they doesn't show as changed initially
  nameFormat.value = settingStore.setting.sharedClient.storageArea.nameFormat;
  logYardLength.value = settingStore.setting.sharedClient.storageArea.logYardLength;
  logYardWidth.value = settingStore.setting.sharedClient.storageArea.logYardWidth;
  logYardSideArea.value = settingStore.setting.sharedClient.storageArea.logYardSideArea;
  minZoom.value = settingStore.setting.sharedClient.map.minZoom ?? 13;
});

const minZoom = ref<number>();
const minZoomInitial = computed(() => settingStore.setting.sharedClient.map.minZoom);
const minZoomChanged = computed(() => minZoom.value !== minZoomInitial.value);
</script>

<template>
  <MainLayout>
    <Card class="style-card">
      <template #title>
        <span>{{ pageTranslations.title }}</span>
      </template>
      <template #content>
        <Tabs value="storagearea">
          <TabList>
            <Tab value="storagearea">{{ pageTranslations.storagearea.title }}</Tab>
            <Tab v-if="authStore.isSystemAdmin" value="featureflags">{{
              pageTranslations.featureflags.title
            }}</Tab>
            <Tab value="map">{{ translations.generic.map }}</Tab>
            <Tab value="reports">{{ pageTranslations.reports.title }}</Tab>
            <Tab value="dashboardComponentTypeConfigs" v-if="authStore.isSystemAdmin">{{
              pageTranslations.dashboards.title
            }}</Tab>
          </TabList>
          <TabPanels>
            <TabPanel class="settings-panel" value="storagearea">
              <form class="systemsettings-form">
                <div class="field" :class="{ changed: nameFormatChanged }">
                  <label for="systemsetting-storagearea-nameformat">{{
                    pageTranslations.storagearea.nameFormat
                  }}</label>
                  <InputText
                    autocomplete="off"
                    v-model:modelValue="nameFormat"
                    autofocus
                    id="systemsetting-storagearea-nameformat"
                    name="nameformat"
                  ></InputText>
                  <Message v-if="nameFormatError" severity="error" variant="simple"
                    >{{ nameFormatError }}
                  </Message>
                </div>

                <Fieldset class="log-fields" :legend="pageTranslations.storagearea.logYard">
                  <div class="field" :class="{ changed: logYardLengthChanged }">
                    <label for="systemsetting-storagearea-logYardLength">{{
                      pageTranslations.storagearea.logYardLength
                    }}</label>
                    <InputGroup>
                      <InputNumber
                        :maxFractionDigits="1"
                        pt:pcInputText:root:autocomplete="off"
                        v-model:modelValue="logYardLength"
                        id="systemsetting-storagearea-logYardLength"
                        name="logYardLength"
                      ></InputNumber>
                      <InputGroupAddon>m</InputGroupAddon>
                    </InputGroup>
                  </div>

                  <div class="field" :class="{ changed: logYardWidthChanged }">
                    <label for="systemsetting-storagearea-logYardWidth">{{
                      pageTranslations.storagearea.logYardWidth
                    }}</label>
                    <InputGroup>
                      <InputNumber
                        :maxFractionDigits="1"
                        pt:pcInputText:root:autocomplete="off"
                        v-model:modelValue="logYardWidth"
                        id="systemsetting-storagearea-logYardWidth"
                        name="logYardWidth"
                      ></InputNumber>
                      <InputGroupAddon>m</InputGroupAddon>
                    </InputGroup>
                  </div>

                  <div class="field" :class="{ changed: logYardSideAreaChanged }">
                    <label for="systemsetting-storagearea-logYardSideArea">{{
                      pageTranslations.storagearea.logYardSideArea
                    }}</label>
                    <InputGroup>
                      <InputNumber
                        :maxFractionDigits="1"
                        pt:pcInputText:root:autocomplete="off"
                        v-model:modelValue="logYardSideArea"
                        id="systemsetting-storagearea-logYardSideArea"
                        name="logYardSideArea"
                      ></InputNumber>
                      <InputGroupAddon>m</InputGroupAddon>
                    </InputGroup>
                  </div>
                </Fieldset>
                <Button
                  class="save-button"
                  :label="translations.generic.save"
                  icon="pi pi-check"
                  @click="save"
                ></Button>
              </form>
            </TabPanel>
            <TabPanel class="settings-panel" value="featureflags">
              <FeatureFlagComponent></FeatureFlagComponent>
            </TabPanel>
            <TabPanel value="map">
              <form class="systemsettings-form">
                <div class="field" :class="{ changed: minZoomChanged }">
                  <label for="systemsetting-map-minzoom">{{ pageTranslations.map.minZoom }}</label>
                  <InputNumber
                    autocomplete="off"
                    v-model:modelValue="minZoom"
                    autofocus
                    id="systemsetting-map-minzoom"
                    name="minzoom"
                    :min="0"
                    :max="24"
                  ></InputNumber>
                </div>
                <Button
                  class="save-button"
                  :label="translations.generic.save"
                  icon="pi pi-check"
                  @click="save"
                ></Button>
              </form>
            </TabPanel>
            <TabPanel class="settings-panel" value="reports">
              <ReportComponent></ReportComponent>
            </TabPanel>
            <TabPanel class="settings-panel" value="dashboardComponentTypeConfigs">
              <DashboardComponentTypeConfig></DashboardComponentTypeConfig>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </template>
    </Card>
  </MainLayout>
</template>

<style scoped>
.settings-panel {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  row-gap: 1rem;
}

.field {
  display: flex;
  flex-direction: column;
}

.log-fields {
  margin-top: 1rem;
  grid-column: 1/4;
}

.save-button {
  margin-top: 1rem;
}

.log-fields :deep(.p-fieldset-content) {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  row-gap: 0.5rem;
  column-gap: 0.5rem;
}
</style>
